/**
 * VRG-720 — parse a `.vcf` the visitor shares, so handing over a contact card beats typing.
 *
 * ## Why a FILE and not the Contact Picker
 *
 * `utils/contactPicker.ts` carries the Contact Picker, and it cannot do this job for two
 * independent reasons: it does not exist on iOS Safari (flag-only, off by default), and even
 * where it does exist its property list is `name`, `email`, `tel`, `address`, `icon` — there
 * is NO organization and no title. So the picker could never supply the fields this exists
 * to collect, on any platform.
 *
 * A `.vcf` carries the whole card — structured name, ORG, TITLE, every email and phone with
 * its WORK/HOME label — and a file input works everywhere: iOS, Android, desktop. On iPhone
 * the route is Contacts → share the card → Save to Files → pick it here.
 *
 * ## Structured N is the point, not a detail
 *
 * `N:Stebbins;Spencer;;;` is `family;given;additional;prefix;suffix`, authored by the person
 * themselves. That is why this path can populate first/last CORRECTLY while a typed full
 * name cannot: splitting `"Spencer Stebbins"` on a space is a guess that breaks on mononyms,
 * compound surnames, family-name-first ordering and particles ("van der Berg"), and iOS has
 * even been reported to fill a single `autocomplete="name"` field in reversed order. We
 * never guess. `firstName`/`lastName` come back ONLY when `N` supplied them.
 *
 * ## What this does NOT do
 *
 * No PHOTO, no ADR, no multi-value arrays. One value per field, chosen by the preference
 * rules below, because the gate has one input per field. Add more when there is somewhere to
 * put it — today `scans` stores `contact_name` and `email` and nothing else, so `company`,
 * `title` and `phone` are parsed and returned but have no column yet.
 */

export interface ParsedVCard {
  /** `FN`, else `N` recomposed as "given family". */
  fullName?: string;
  /** From `N` ONLY — never guessed by splitting `fullName`. */
  firstName?: string;
  lastName?: string;
  /** Preferred then WORK then first. */
  email?: string;
  /** `ORG` — its first component; the rest are org units, not the company. */
  company?: string;
  /** `TITLE`. */
  title?: string;
  /** Preferred then CELL then WORK then first. */
  phone?: string;
}

/** The practical ceiling for a contact card; anything larger is not one. */
export const VCARD_MAX_BYTES = 512 * 1024;

interface VCardLine {
  name: string;
  params: Record<string, string[]>;
  value: string;
}

/**
 * Undo RFC 6350 line folding, which is NOT cosmetic — Apple folds long lines, so an ORG or
 * a long email arrives split across lines and a naive line-by-line read truncates it.
 * A continuation is any line beginning with a space or a tab; the marker is removed and the
 * remainder joined to the previous line with nothing between.
 */
const unfold = (text: string): string[] => {
  const out: string[] = [];
  for (const raw of text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n')) {
    if (/^[ \t]/.test(raw) && out.length) out[out.length - 1] += raw.slice(1);
    else out.push(raw);
  }
  return out;
};

/** `\,` `\;` `\n` `\\` are escaped inside a vCard value. */
const unescapeValue = (value: string): string =>
  value.replace(/\\([,;nN\\])/g, (_m, c) => (c === 'n' || c === 'N' ? '\n' : c));

/**
 * Split on a delimiter that is NOT backslash-escaped.
 *
 * Needed because a surname can legitimately contain a semicolon-escaped character, and a
 * plain `split(';')` would tear `N` apart at the wrong place.
 */
const splitUnescaped = (value: string, delimiter: string): string[] => {
  const parts: string[] = [];
  let current = '';
  for (let i = 0; i < value.length; i += 1) {
    const char = value[i];
    if (char === '\\' && i + 1 < value.length) {
      current += char + value[i + 1];
      i += 1;
    } else if (char === delimiter) {
      parts.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  parts.push(current);
  return parts;
};

/**
 * `ITEM1.TEL;TYPE=CELL;TYPE=VOICE:+1...` → name TEL, params {TYPE:[CELL,VOICE]}, value +1...
 *
 * The `ITEM1.` group prefix is Apple's and is dropped: it only ties a property to an
 * `X-ABLabel`, which we do not read. Parameters may repeat (two `TYPE=`) or be
 * comma-packed (`TYPE=WORK,VOICE`); both forms are flattened into one list, because Apple
 * emits the first and Google the second.
 */
const parseLine = (line: string): VCardLine | null => {
  // The colon splitting name+params from value is the FIRST unescaped one; a value may
  // itself contain colons (a URL), so indexOf is correct here and lastIndexOf is not.
  const colon = line.indexOf(':');
  if (colon < 0) return null;
  const head = line.slice(0, colon);
  const value = line.slice(colon + 1);

  const segments = splitUnescaped(head, ';');
  let name = (segments.shift() ?? '').trim();
  const dot = name.indexOf('.');
  if (dot >= 0) name = name.slice(dot + 1);

  const params: Record<string, string[]> = {};
  for (const segment of segments) {
    const eq = segment.indexOf('=');
    // A bare parameter is vCard 2.1 shorthand (`TEL;CELL:` rather than `TEL;TYPE=CELL:`),
    // which Apple still emits on older exports. Treat it as a TYPE.
    const key = (eq < 0 ? 'TYPE' : segment.slice(0, eq)).trim().toUpperCase();
    const raw = eq < 0 ? segment : segment.slice(eq + 1);
    const values = raw
      .split(',')
      .map(v => v.trim().replace(/^"|"$/g, '').toUpperCase())
      .filter(Boolean);
    params[key] = [...(params[key] ?? []), ...values];
  }

  return { name: name.toUpperCase(), params, value };
};

const types = (line: VCardLine): string[] => [
  ...(line.params.TYPE ?? []),
  // vCard 4.0 moved preference out of TYPE and into its own PREF parameter.
  ...(line.params.PREF ? ['PREF'] : []),
];

/**
 * Pick one line from several candidates: an explicit preference wins, then the first
 * matching type in `order`, then the first line at all.
 */
const pick = (lines: VCardLine[], order: string[]): VCardLine | undefined => {
  if (!lines.length) return undefined;
  const preferred = lines.find(l => types(l).includes('PREF'));
  if (preferred) return preferred;
  for (const type of order) {
    const match = lines.find(l => types(l).includes(type));
    if (match) return match;
  }
  return lines[0];
};

const clean = (value?: string): string | undefined => {
  const trimmed = unescapeValue(value ?? '').trim();
  return trimmed || undefined;
};

/**
 * Parse the FIRST card in a `.vcf`.
 *
 * A file can legally hold many cards (an exported group). We take the first and ignore the
 * rest rather than guessing which one is "them" — the visitor is sharing their own card, and
 * a UI that asked them to disambiguate would cost more than the field it fills. Returns
 * `null` when the text is not a vCard at all, so the caller can say so rather than silently
 * filling nothing.
 */
export const parseVCard = (text: string): ParsedVCard | null => {
  const lines = unfold(text);
  const begin = lines.findIndex(l => /^BEGIN:VCARD\s*$/i.test(l.trim()));
  if (begin < 0) return null;
  const endOffset = lines.slice(begin + 1).findIndex(l => /^END:VCARD\s*$/i.test(l.trim()));
  const body = lines.slice(begin + 1, endOffset < 0 ? undefined : begin + 1 + endOffset);

  const parsed = body
    .map(parseLine)
    .filter((l): l is VCardLine => l !== null)
    // A quoted-printable value (vCard 2.1) would need decoding before it is readable; we do
    // not decode it, so dropping it is better than surfacing "=C3=A9" into a name field.
    .filter(l => !(l.params.ENCODING ?? []).some(e => e === 'QUOTED-PRINTABLE'));

  const byName = (wanted: string) => parsed.filter(l => l.name === wanted);

  const nLine = byName('N')[0];
  let firstName: string | undefined;
  let lastName: string | undefined;
  if (nLine) {
    const [family, given] = splitUnescaped(nLine.value, ';');
    lastName = clean(family);
    firstName = clean(given);
  }

  const fnLine = byName('FN')[0];
  const fullName =
    clean(fnLine?.value) ??
    ([firstName, lastName].filter(Boolean).join(' ').trim() || undefined);

  const emailLine = pick(byName('EMAIL'), ['WORK', 'INTERNET', 'HOME']);
  const telLine = pick(byName('TEL'), ['CELL', 'MOBILE', 'IPHONE', 'WORK', 'HOME']);

  // ORG is `company;unit;sub-unit`. Only the first component is the organisation name.
  const orgLine = byName('ORG')[0];
  const company = orgLine ? clean(splitUnescaped(orgLine.value, ';')[0]) : undefined;

  const result: ParsedVCard = {
    fullName,
    firstName,
    lastName,
    email: clean(emailLine?.value),
    company,
    title: clean(byName('TITLE')[0]?.value),
    phone: clean(telLine?.value),
  };

  // Nothing usable is the same as not a vCard, as far as the caller is concerned.
  return Object.values(result).some(Boolean) ? result : null;
};

/** True when a picked file plausibly IS a vCard — extension or MIME, since iOS sets neither reliably. */
export const isVCardFile = (file: File): boolean =>
  /\.vcf$/i.test(file.name) ||
  file.type === 'text/vcard' ||
  file.type === 'text/x-vcard' ||
  file.type === 'text/directory';
